import React, { useState, useEffect } from 'react';
import {
  Search, Package, ShoppingCart, Eye, FileText,
  Filter, Grid, List, TrendingUp, AlertCircle, Check
} from 'lucide-react';
import api, { API_URL } from '../../services/api';

const CatalogueProduits = ({ boutiqueId, boutiqueName }) => {
  const [produits, setProduits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categorieSelectionnee, setCategorieSelectionnee] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [produitSelectionne, setProduitSelectionne] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDevisModal, setShowDevisModal] = useState(false);
  const [panierDevis, setPanierDevis] = useState([]);

  useEffect(() => {
    loadCategories();
    loadProduits();
  }, [boutiqueId, categorieSelectionnee, searchTerm]);

  const loadCategories = async () => {
    try {
      const response = await api.get(`${API_URL}/catalogue/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  const loadProduits = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (boutiqueId) params.append('boutique_id', boutiqueId);
      if (categorieSelectionnee) params.append('categorie', categorieSelectionnee);
      if (searchTerm) params.append('search', searchTerm);

      const response = await api.get(`${API_URL}/catalogue/produits?${params}`);
      setProduits(response.data);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
    } finally {
      setLoading(false);
    }
  };

  const voirDetails = async (produitId) => {
    try {
      const response = await api.get(
        `${API_URL}/catalogue/produits/${produitId}?boutique_id=${boutiqueId}`
      );
      setProduitSelectionne(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Erreur chargement détails:', error);
      alert('Erreur lors du chargement des détails');
    }
  };

  const ajouterAuPanier = (produit) => {
    const existe = panierDevis.find(p => p.bijou_id === produit.id);
    if (existe) {
      setPanierDevis(panierDevis.map(p => 
        p.bijou_id === produit.id 
          ? { ...p, quantite: p.quantite + 1 }
          : p
      ));
    } else {
      setPanierDevis([...panierDevis, {
        bijou_id: produit.id,
        nom: produit.nom,
        reference: produit.reference,
        prix_unitaire: produit.prix_vente,
        quantite: 1,
        stock_disponible: produit.stock_boutique || 0
      }]);
    }
  };

  const creerDevis = async (clientId, notes) => {
    try {
      const response = await api.post(`${API_URL}/catalogue/devis`, {
        boutique_id: boutiqueId,
        client_id: clientId,
        produits: panierDevis.map(p => ({
          bijou_id: p.bijou_id,
          quantite: p.quantite,
          prix_unitaire: p.prix_unitaire
        })),
        notes: notes,
        created_by: 1 // À remplacer par l'ID de l'utilisateur connecté
      });

      alert(`Devis ${response.data.numero_devis} créé avec succès !`);
      setPanierDevis([]);
      setShowDevisModal(false);
    } catch (error) {
      console.error('Erreur création devis:', error);
      alert('Erreur lors de la création du devis');
    }
  };

  const getStockBadge = (produit) => {
    const stock = produit.stock_boutique || 0;
    if (stock > 10) {
      return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">En stock ({stock})</span>;
    } else if (stock > 0) {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">Stock limité ({stock})</span>;
    } else {
      const stockTotal = produit.stock_total || 0;
      if (stockTotal > 0) {
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Disponible ailleurs ({stockTotal})</span>;
      }
      return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Sur commande</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Catalogue Produits</h2>
            <p className="text-gray-600 mt-1">{boutiqueName}</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowDevisModal(true)}
              disabled={panierDevis.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="h-5 w-5" />
              <span>Créer un devis ({panierDevis.length})</span>
            </button>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <select
            value={categorieSelectionnee}
            onChange={(e) => setCategorieSelectionnee(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Toutes les catégories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.nom} ({cat.nombre_produits})
              </option>
            ))}
          </select>

          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'}`}
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'}`}
            >
              <List className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Liste des produits */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des produits...</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'space-y-4'}>
          {produits.map(produit => (
            <div key={produit.id} className={`bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow ${viewMode === 'list' ? 'flex' : ''}`}>
              {/* Image */}
              <div className={`bg-gray-200 ${viewMode === 'grid' ? 'h-48' : 'w-32 h-32'} flex items-center justify-center`}>
                {produit.image_url ? (
                  <img src={produit.image_url} alt={produit.nom} className="w-full h-full object-cover" />
                ) : (
                  <Package className="h-12 w-12 text-gray-400" />
                )}
              </div>

              {/* Contenu */}
              <div className="p-4 flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{produit.nom}</h3>
                    <p className="text-sm text-gray-500">{produit.reference}</p>
                  </div>
                  {getStockBadge(produit)}
                </div>

                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{produit.description}</p>

                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-indigo-600">
                    {parseFloat(produit.prix_vente).toFixed(2)} €
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => voirDetails(produit.id)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                      title="Voir les détails"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => ajouterAuPanier(produit)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                      title="Ajouter au devis"
                    >
                      <ShoppingCart className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {produits.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg">
          <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucun produit trouvé</p>
        </div>
      )}

      {/* Modal Détails Produit */}
      {showDetailModal && produitSelectionne && (
        <DetailProduitModal
          produit={produitSelectionne}
          boutiqueId={boutiqueId}
          onClose={() => setShowDetailModal(false)}
          onAjouterAuPanier={ajouterAuPanier}
        />
      )}

      {/* Modal Création Devis */}
      {showDevisModal && (
        <CreationDevisModal
          panier={panierDevis}
          boutiqueName={boutiqueName}
          onClose={() => setShowDevisModal(false)}
          onCreer={creerDevis}
          onModifierQuantite={(bijouId, nouvelleQuantite) => {
            setPanierDevis(panierDevis.map(p =>
              p.bijou_id === bijouId ? { ...p, quantite: nouvelleQuantite } : p
            ));
          }}
          onSupprimer={(bijouId) => {
            setPanierDevis(panierDevis.filter(p => p.bijou_id !== bijouId));
          }}
        />
      )}
    </div>
  );
};

// Composant Modal Détails Produit
const DetailProduitModal = ({ produit, boutiqueId, onClose, onAjouterAuPanier }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{produit.produit.nom}</h2>
              <p className="text-gray-600">{produit.produit.reference}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <span className="text-2xl">&times;</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Image */}
            <div className="bg-gray-200 h-64 rounded-lg flex items-center justify-center">
              {produit.produit.image_url ? (
                <img src={produit.produit.image_url} alt={produit.produit.nom} className="w-full h-full object-cover rounded-lg" />
              ) : (
                <Package className="h-24 w-24 text-gray-400" />
              )}
            </div>

            {/* Informations */}
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Prix</h3>
                <p className="text-3xl font-bold text-indigo-600">
                  {parseFloat(produit.produit.prix_vente).toFixed(2)} €
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600">{produit.produit.description || 'Aucune description'}</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Catégorie</h3>
                <p className="text-gray-600">{produit.produit.categorie_nom || 'Non catégorisé'}</p>
              </div>

              <button
                onClick={() => {
                  onAjouterAuPanier(produit.produit);
                  onClose();
                }}
                className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <ShoppingCart className="h-5 w-5" />
                <span>Ajouter au devis</span>
              </button>
            </div>
          </div>

          {/* Stock par boutique */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Stock disponible</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {produit.stocks && produit.stocks.length > 0 ? (
                produit.stocks.map(stock => (
                  <div key={stock.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{stock.boutique_nom}</p>
                      <p className="text-sm text-gray-600">{stock.boutique_ville}</p>
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full font-semibold">
                      {stock.quantite} unités
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 col-span-2">Aucun stock disponible</p>
              )}
            </div>
          </div>

          {/* Traçabilité - Mouvements récents */}
          {produit.mouvements && produit.mouvements.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Historique des mouvements</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {produit.mouvements.slice(0, 10).map(mouvement => (
                  <div key={mouvement.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{mouvement.type_mouvement}</p>
                      <p className="text-gray-600">
                        {mouvement.boutique_source_nom && `De: ${mouvement.boutique_source_nom}`}
                        {mouvement.boutique_destination_nom && ` → Vers: ${mouvement.boutique_destination_nom}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{mouvement.quantite > 0 ? '+' : ''}{mouvement.quantite}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(mouvement.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ventes récentes */}
          {produit.ventes && produit.ventes.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Ventes récentes</h3>
              <div className="space-y-2">
                {produit.ventes.slice(0, 5).map(vente => (
                  <div key={vente.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium text-gray-900">{vente.client_nom || 'Client anonyme'}</p>
                      <p className="text-gray-600">{vente.boutique_nom}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">{parseFloat(vente.prix_vente).toFixed(2)} €</p>
                      <p className="text-xs text-gray-500">
                        {new Date(vente.date_vente).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant Modal Création Devis
const CreationDevisModal = ({ panier, boutiqueName, onClose, onCreer, onModifierQuantite, onSupprimer }) => {
  const [clientId, setClientId] = useState('');
  const [notes, setNotes] = useState('');

  const total = panier.reduce((sum, p) => sum + (p.prix_unitaire * p.quantite), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Créer un devis</h2>
              <p className="text-gray-600">{boutiqueName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <span className="text-2xl">&times;</span>
            </button>
          </div>

          {/* Produits */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Produits ({panier.length})</h3>
            <div className="space-y-3">
              {panier.map(produit => (
                <div key={produit.bijou_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{produit.nom}</p>
                    <p className="text-sm text-gray-600">{produit.reference}</p>
                    {produit.stock_disponible === 0 && (
                      <p className="text-xs text-orange-600 flex items-center mt-1">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Sur commande (pas de stock)
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      min="1"
                      value={produit.quantite}
                      onChange={(e) => onModifierQuantite(produit.bijou_id, parseInt(e.target.value) || 1)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                    />
                    <span className="w-24 text-right font-semibold">
                      {(produit.prix_unitaire * produit.quantite).toFixed(2)} €
                    </span>
                    <button
                      onClick={() => onSupprimer(produit.bijou_id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      &times;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
            <div className="flex items-center justify-between text-lg font-bold">
              <span>Total TTC</span>
              <span className="text-indigo-600">{total.toFixed(2)} €</span>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Remarques ou conditions particulières..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={() => onCreer(clientId || null, notes)}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Créer le devis
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogueProduits;
